import {useMemo} from 'react'
import {useIntl} from 'react-intl'

const isEmpty = (val) => val === undefined || val === null || val === ''

export const useAgeRange = (from, to) => {
    const {formatMessage} = useIntl()

    return useMemo(() => {
        if (isEmpty(from) && isEmpty(to)) return null

        const isFromValid = !isEmpty(from)
        const isToValid = !isEmpty(to) && Number(to) !== 0
        const fromNum = Number(from)
        const toNum = Number(to)

        if (fromNum > 0 && fromNum >= toNum) return null

        let useYears = false
        if (isFromValid && isToValid) {
            if (fromNum === 0) {
                // If starting from 0, only use years if 'to' is 24, 36, etc.
                useYears = toNum >= 24 && toNum % 12 === 0
            } else {
                // Both must be divisible by 12 and > 0
                useYears = fromNum % 12 === 0 && toNum % 12 === 0
            }
        } else {
            // Single value: 12 stays months, 24+ becomes years
            const activeVal = isFromValid ? fromNum : toNum
            useYears = activeVal >= 24 && activeVal % 12 === 0
        }

        const fromVal = useYears ? fromNum / 12 : fromNum
        const toVal = useYears ? toNum / 12 : toNum

        // Variant: Up to y
        if (!isFromValid && isToValid) {
            return useYears
                ? formatMessage(
                      {
                          id: 'leap.age_range.up_to.years',
                          defaultMessage: 'Up to {to, plural, one {# year} other {# years}}'
                      },
                      {to: toVal}
                  )
                : formatMessage(
                      {
                          id: 'leap.age_range.up_to.months',
                          defaultMessage: 'Up to {to, plural, one {# month} other {# months}}'
                      },
                      {to: toVal}
                  )
        }

        // Variant: From x
        if (isFromValid && !isToValid) {
            if (fromNum === 0) {
                return formatMessage({
                    id: 'leap.age_range.from_birth',
                    defaultMessage: 'From Birth'
                })
            }
            return useYears
                ? formatMessage(
                      {
                          id: 'leap.age_range.from.years',
                          defaultMessage: 'From {from, plural, one {# year} other {# years}}'
                      },
                      {from: fromVal}
                  )
                : formatMessage(
                      {
                          id: 'leap.age_range.from.months',
                          defaultMessage: 'From {from, plural, one {# month} other {# months}}'
                      },
                      {from: fromVal}
                  )
        }

        if (!isFromValid || !isToValid) return null

        // Variant: From x to y
        if (useYears) {
            return formatMessage(
                {
                    id: 'leap.age_range.from_to.years',
                    defaultMessage: 'From {from} to {to, plural, one {# year} other {# years}}'
                },
                {from: fromVal, to: toVal}
            )
        }

        // Default to months to months
        return formatMessage(
            {
                id: 'leap.age_range.from_to.months',
                defaultMessage: 'From {from} to {to, plural, one {# month} other {# months}}'
            },
            {from: fromVal, to: toVal}
        )
    }, [from, to, formatMessage])
}
